import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, ShieldCheck, HeartHandshake, ArrowRight, MessageCircle, WheatOff, MilkOff, CandyOff, Sparkles, Truck, CreditCard, Percent, Leaf, Instagram } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1724441980123-aca7911329d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const GRANEL_BANNER = "https://images.unsplash.com/photo-1633633689014-94ad6325c366?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const KITS_BANNER = "https://images.unsplash.com/photo-1645567454567-901dc409551b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const CAKE_IMG = "https://images.unsplash.com/photo-1568827999250-3f6afff96e66?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const TRUST = [
  { icon: BadgeCheck, title: "Produtos de qualidade", text: "Selecionamos o melhor para você" },
  { icon: ShieldCheck, title: "Compra 100% segura", text: "Seus dados protegidos" },
  { icon: HeartHandshake, title: "Atendimento humanizado", text: "Estamos aqui para ajudar" },
];

const CATEGORIES = [
  { key: "granel", label: "Granel", img: "https://images.unsplash.com/photo-1769255484239-2f2f7278e233?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { key: "suplementos", label: "Suplementos", img: "https://images.unsplash.com/photo-1763668177859-0ed5669a795e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { key: "chas", label: "Chás e Ervas", img: "https://images.unsplash.com/photo-1563636680-28d36aeb83a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { key: "alimentos", label: "Alimentos Naturais", img: "https://images.unsplash.com/photo-1724441980123-aca7911329d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { key: "cosmeticos", label: "Cosméticos Naturais", img: "https://images.unsplash.com/photo-1600857544200-b2f666a9a2ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
  { key: "kits", label: "Kits e Presentes", img: "https://images.unsplash.com/photo-1645567454567-901dc409551b?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" },
];

const CAKE_OPTS = [
  { icon: WheatOff, label: "Sem glúten" },
  { icon: MilkOff, label: "Sem lactose" },
  { icon: CandyOff, label: "Sem açúcar" },
  { icon: Sparkles, label: "Outros sabores e tamanhos" },
];

const BENEFITS = [
  { icon: Truck, title: "Frete rápido", text: "Para toda a região" },
  { icon: CreditCard, title: "Parcele em até 6x", text: "Sem juros no cartão" },
  { icon: Percent, title: "5% OFF no PIX", text: "Desconto especial" },
  { icon: Leaf, title: "Produtos naturais", text: "E de qualidade" },
];

const INSTA = [
  "https://images.unsplash.com/photo-1724441980123-aca7911329d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1563636680-28d36aeb83a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1588413333412-82148535db53?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1759006249055-8c4030a2d56a?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1769255484239-2f2f7278e233?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1587049352824-f7e128d4ebe5?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
];

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]));
  }, []);

  const featured = [...(Array.isArray(products) ? products : [])]
    .sort((a, b) => (b.bestseller ? 1 : 0) - (a.bestseller ? 1 : 0))
    .slice(0, 6);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-alabaster">
        <Leaf className="pointer-events-none absolute left-8 top-12 h-24 w-24 rotate-12 text-clay/10" />
        <Leaf className="pointer-events-none absolute bottom-10 left-1/4 h-16 w-16 -rotate-45 text-clay/10" />
        <div className="mx-auto grid max-w-[1280px] items-center gap-8 px-4 py-10 sm:px-6 md:grid-cols-2 md:py-0">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}
            className="md:py-16 md:pr-10 lg:py-20">
            <h1 className="font-serif text-5xl font-semibold leading-[1.02] text-forest-deep lg:text-6xl">
              Alimentação<br />saudável
              <span className="mt-1 block font-normal italic text-clay">para uma vida mais leve!</span>
            </h1>
            <p className="mt-5 max-w-sm font-sans text-base text-ink/70">Produtos naturais selecionados para mais saúde e equilíbrio no seu dia a dia.</p>
            <Link to="/loja" data-testid="hero-shop-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-forest-deep px-7 py-3.5 font-sans text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-clay">
              Compre agora <ArrowRight size={16} />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}
            className="h-64 overflow-hidden rounded-2xl md:h-[440px]">
            <img src={HERO_IMG} alt="Produtos naturais" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 sm:px-6">
      {/* TRUST */}
      <section className="grid grid-cols-1 gap-4 py-10 sm:grid-cols-3">
        {TRUST.map((t) => (
          <div key={t.title} className="flex items-center gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-clay/30 text-clay"><t.icon size={20} /></div>
            <div>
              <p className="font-sans text-sm font-semibold text-forest-deep">{t.title}</p>
              <p className="font-sans text-xs text-ink/55">{t.text}</p>
            </div>
          </div>
        ))}
      </section>

      {/* CATEGORIES */}
      <section className="py-6">
        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-serif text-2xl font-semibold text-forest-deep sm:text-3xl">Compre por categoria</h2>
          <Link to="/loja" className="font-sans text-sm text-clay hover:underline">Ver todas as categorias</Link>
        </div>
        <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
          {CATEGORIES.map((c) => (
            <Link key={c.key} to={`/loja?cat=${c.key}`} data-testid={`category-tile-${c.key}`} className="group flex flex-col items-center gap-3 text-center">
              <div className="aspect-square w-full overflow-hidden rounded-full border border-borderlight bg-white">
                <img src={c.img} alt={c.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </div>
              <span className="font-sans text-xs font-medium text-ink transition-colors group-hover:text-clay sm:text-sm">{c.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* PROMO BANNERS */}
      <section className="grid gap-4 py-8 md:grid-cols-2">
        <Reveal>
          <div className="relative flex h-full items-center overflow-hidden rounded-2xl bg-forest-deep p-8 text-alabaster">
            <div className="relative z-10 max-w-[55%]">
              <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-ochre">Produtos</h3>
              <p className="mt-1 font-serif text-3xl font-semibold">A Granel</p>
              <p className="mt-2 font-sans text-sm text-alabaster/70">Mais economia para o seu dia a dia!</p>
              <Link to="/loja?cat=granel" data-testid="banner-granel" className="mt-5 inline-block rounded-lg border border-alabaster/40 px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-alabaster hover:text-forest-deep">Ver produtos</Link>
            </div>
            <img src={GRANEL_BANNER} alt="Granel" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-90" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative flex h-full items-center overflow-hidden rounded-2xl bg-sand-elevated p-8">
            <div className="relative z-10 max-w-[55%]">
              <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-clay">Kits</h3>
              <p className="mt-1 font-serif text-3xl font-semibold text-forest-deep">Especiais</p>
              <p className="mt-2 font-sans text-sm text-ink/60">Mais saúde, mais bem-estar!</p>
              <Link to="/loja?cat=kits" data-testid="banner-kits" className="mt-5 inline-block rounded-lg bg-forest-deep px-5 py-2.5 font-sans text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-clay">Conheça</Link>
            </div>
            <img src={KITS_BANNER} alt="Kits" className="absolute right-0 top-0 h-full w-1/2 object-cover" />
          </div>
        </Reveal>
      </section>

      {/* FEATURED */}
      <section className="py-8">
        <div className="mb-6 flex items-end justify-between">
          <h2 className="font-serif text-2xl font-semibold text-forest-deep sm:text-3xl">Mais vendidos</h2>
          <Link to="/loja" className="font-sans text-sm text-clay hover:underline">Ver todos</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* BENEFITS STRIP */}
      <section className="grid grid-cols-2 gap-4 border-y border-borderlight py-8 lg:grid-cols-4">
        {BENEFITS.map((b) => (
          <div key={b.title} className="flex items-center gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-sand-elevated text-clay"><b.icon size={20} /></div>
            <div>
              <p className="font-sans text-sm font-semibold text-forest-deep">{b.title}</p>
              <p className="font-sans text-xs text-ink/55">{b.text}</p>
            </div>
          </div>
        ))}
      </section>

      {/* BOLOS POR ENCOMENDA */}
      <Reveal>
        <section className="my-8 overflow-hidden rounded-2xl border border-borderlight bg-sand-elevated">
          <div className="grid gap-0 lg:grid-cols-2">
            <div className="grid grid-cols-1 items-stretch sm:grid-cols-2">
              <div className="p-8 sm:p-10">
                <h2 className="font-serif text-3xl font-semibold text-forest-deep">Bolos por Encomenda</h2>
                <p className="mt-2 font-sans text-sm text-ink/70">Bolos especiais feitos para o seu momento!</p>
                <p className="mt-4 font-sans text-sm text-ink/60">Encomende do seu jeito: sem glúten, sem lactose e/ou sem açúcar. Você escolhe!</p>
                <a href="https://wa.me/5519995839691" target="_blank" rel="noreferrer" data-testid="cake-order-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-lg bg-forest-deep px-6 py-3 font-sans text-sm font-medium text-white transition-colors hover:bg-clay">
                  <MessageCircle size={16} /> Fazer encomenda
                </a>
              </div>
              <div className="min-h-[220px]"><img src={CAKE_IMG} alt="Bolos" className="h-full w-full object-cover" /></div>
            </div>
            <div className="border-t border-borderlight p-8 sm:p-10 lg:border-l lg:border-t-0">
              <h3 className="font-serif text-xl font-semibold text-forest-deep">Personalize do seu jeito:</h3>
              <ul className="mt-5 space-y-4">
                {CAKE_OPTS.map((o) => (
                  <li key={o.label} className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-clay/30 text-clay"><o.icon size={18} /></div>
                    <span className="font-sans text-sm text-ink/80">{o.label}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 font-sans text-sm text-ink/60">Feito com ingredientes naturais e muito amor.</p>
            </div>
          </div>
        </section>
      </Reveal>

      {/* INSTAGRAM */}
      <section className="pb-16">
        <div className="mb-5 flex items-center gap-2">
          <Instagram size={22} className="text-clay" />
          <div>
            <h2 className="font-serif text-xl font-semibold text-forest-deep">Siga nosso Instagram</h2>
            <p className="font-sans text-xs text-ink/55">@multinativasaudenatural</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {INSTA.map((src, i) => (
            <a key={i} href="#" className="group relative aspect-square overflow-hidden rounded-xl">
              <img src={src} alt="Instagram" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 flex items-center justify-center bg-forest-deep/0 transition-colors group-hover:bg-forest-deep/40">
                <Instagram size={22} className="text-white opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
            </a>
          ))}
        </div>
      </section>
      </div>
    </>
  );
}
